import {createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  fetchZadSalesHistoryProductDetail,
  fetchZadSalesHistoryProducts,
  fetchZadSalesHistorySales,
  fetchZadSalesHistorySummary,
} from './service'
import {ZadSalesHistoryState} from './types'

const initialState: ZadSalesHistoryState = {
  summary: null,
  summaryLoading: false,
  summaryInitialized: false,
  summaryError: null,
  summaryErrorCode: null,
  products: null,
  productsLoading: false,
  productsInitialized: false,
  productsError: null,
  sales: null,
  salesLoading: false,
  salesInitialized: false,
  salesError: null,
  productDetail: null,
  productDetailLoading: false,
  productDetailError: null,
  requestKey: null,
}

function isStale(state: ZadSalesHistoryState, requestKey?: string) {
  return Boolean(state.requestKey && requestKey && state.requestKey !== requestKey)
}

const zadSalesHistorySlice = createSlice({
  name: 'zadSalesHistory',
  initialState,
  reducers: {
    clearZadSalesHistoryProductDetail(state) {
      state.productDetail = null
      state.productDetailError = null
      state.productDetailLoading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZadSalesHistorySummary.pending, (state, action) => {
        state.requestKey = action.meta.arg.requestKey
        state.summaryLoading = true
        state.summaryError = null
        state.summaryErrorCode = null
      })
      .addCase(fetchZadSalesHistorySummary.fulfilled, (state, action) => {
        if (isStale(state, action.payload.requestKey)) return
        state.summaryLoading = false
        state.summaryInitialized = true
        state.summary = action.payload.report
      })
      .addCase(fetchZadSalesHistorySummary.rejected, (state, action) => {
        if (isStale(state, action.meta.arg.requestKey)) return
        const err = action.payload as ZadApiError | undefined
        state.summaryLoading = false
        state.summaryInitialized = true
        state.summaryError = err?.error || 'REQUEST_FAILED'
        state.summaryErrorCode = err?.errorCode || null
      })
      .addCase(fetchZadSalesHistoryProducts.pending, (state, action) => {
        state.requestKey = action.meta.arg.requestKey
        state.productsLoading = true
        state.productsError = null
      })
      .addCase(fetchZadSalesHistoryProducts.fulfilled, (state, action) => {
        if (isStale(state, action.payload.requestKey)) return
        state.productsLoading = false
        state.productsInitialized = true
        state.products = action.payload.report
      })
      .addCase(fetchZadSalesHistoryProducts.rejected, (state, action) => {
        if (isStale(state, action.meta.arg.requestKey)) return
        const err = action.payload as ZadApiError | undefined
        state.productsLoading = false
        state.productsInitialized = true
        state.productsError = err?.error || 'REQUEST_FAILED'
      })
      .addCase(fetchZadSalesHistorySales.pending, (state, action) => {
        state.requestKey = action.meta.arg.requestKey
        state.salesLoading = true
        state.salesError = null
      })
      .addCase(fetchZadSalesHistorySales.fulfilled, (state, action) => {
        if (isStale(state, action.payload.requestKey)) return
        state.salesLoading = false
        state.salesInitialized = true
        state.sales = action.payload.report
      })
      .addCase(fetchZadSalesHistorySales.rejected, (state, action) => {
        if (isStale(state, action.meta.arg.requestKey)) return
        const err = action.payload as ZadApiError | undefined
        state.salesLoading = false
        state.salesInitialized = true
        state.salesError = err?.error || 'REQUEST_FAILED'
      })
      .addCase(fetchZadSalesHistoryProductDetail.pending, (state) => {
        state.productDetailLoading = true
        state.productDetailError = null
      })
      .addCase(fetchZadSalesHistoryProductDetail.fulfilled, (state, action) => {
        if (isStale(state, action.payload.requestKey)) return
        state.productDetailLoading = false
        state.productDetail = action.payload.report
      })
      .addCase(fetchZadSalesHistoryProductDetail.rejected, (state, action) => {
        if (isStale(state, action.meta.arg.requestKey)) return
        const err = action.payload as ZadApiError | undefined
        state.productDetailLoading = false
        state.productDetailError = err?.error || 'REQUEST_FAILED'
      })
  },
})

export const {clearZadSalesHistoryProductDetail} = zadSalesHistorySlice.actions
export default zadSalesHistorySlice.reducer
